/**
 * SFOC viewer - the SFOC model of the Model Viewer: the chart area.
 *
 * Its controls are in the shared sidebar, like every other Explore tab: the
 * Model Viewer's Query card (ships and date range, shared with Service Power -
 * `features/ship/model-selection.ts`) and SFOC's curve matrix under it
 * (`sfoc-curve-picker.ts`). So the sidebar, divider and chart never move when
 * you switch model.
 *
 * The chart is Explore's chart: drawn by the same renderer a dashboard tile
 * uses (`renderTile`), from a line response built here out of the fitted
 * curves, with the same chart gear editing its own `PlotState`
 * (`sfoc-chart-style.ts`). What only SFOC draws - load guides, per-fuel y-bands,
 * dashed fallback curves, the load-usage shading - is added through the
 * renderer's `decorate` hook rather than a second chart implementation.
 *
 * Lit owns the card; the chart is built imperatively into a container Lit
 * leaves alone, so uPlot's injected DOM survives re-renders.
 */

import { html, nothing, svg, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import type uPlot from 'uplot';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import {
  activeModelTab,
  activeTab,
  modelShips,
  modelStart,
  modelEnd,
  sfocData,
  sfocDate,
  sfocSelected,
  sfocLayout,
} from '../../state/store.ts';
import { getCssVar, themeColors } from '../plot/render/chart-dom.ts';
import { heatmapDrawHook } from '../plot/render/render-heatmap.ts';
import { drawBranding } from '../plot/render/overlays.ts';
import { formatNum, withAlpha } from '../plot/render/format.ts';
import { buildSeriesColorer } from '../plot/plot-constants.ts';
import { applyChartFontVar } from '../plot/render/uplot-helpers.ts';
import { renderTile, type TileChart } from '../dashboard/render-tile.ts';
import { gridColumns } from '../plot/render/render-grid.ts';
import { shipLabel } from '../../utils/ships.ts';
import { downloadXlsx, type XlsxCell } from '../../utils/xlsx.ts';
import type { PlotResponse, SfocCurve, SfocCurveVersion } from '../../api/types.ts';
import '../plot/plot-settings-menu.ts';
import { sfocChartStyle, sfocPlotState } from './sfoc-chart-style.ts';
import {
  GENERIC_ENGINE,
  curveKey,
  fuelShade,
  fuelVar,
  getCurve,
  shipMatrix,
  versionAt,
} from './sfoc-selection.ts';
import { curveDateFor, ensurePickedLoaded } from './sfoc-data.ts';
import './sfoc-time-slicer.ts';
import type { SliceTick } from './sfoc-time-slicer.ts';
import './sfoc-curve-picker.ts';
import {
  SFOC_BANDS,
  binHeatmap,
  binLoad,
  ensureMeasured,
  measuredCache,
  measuredColumn,
  measuredKey,
  sfocMeasured,
  sfocSlice,
  sfocTables,
  type MeasuredPoints,
} from './sfoc-measured.ts';

/** Engine load domain (percent) the curves are sampled over. Engines don't run
 * meaningfully below ~20%, so we clamp the view to the useful range. The axis
 * runs to LOAD_MAX, but the fitted curves are only trustworthy up to
 * LOAD_PLOT_MAX - beyond that we draw nothing (gap), not an extrapolation. */
const LOAD_MIN = 20;
const LOAD_MAX = 100;
const LOAD_PLOT_MAX = 90;
const LOAD_STEP = 1;

/** Reference load lines (%) drawn as vertical guides to read the plot against. */
const LOAD_GUIDES = [50, 80];

/** Load points (%) tabulated under the plot, one column each. */
const TABLE_LOADS = [30, 40, 50, 60, 70, 80, 90];

/** Download arrow into a tray - the dashboard toolbar's Export glyph. */
const DOWNLOAD_GLYPH = svg`<svg
  class="chart-action-glyph btn-glyph"
  viewBox="0 0 24 24"
  fill="none"
  aria-hidden="true"
>
  <path
    d="M12 4v11M7.5 10.5 12 15l4.5-4.5M4 17v2a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-2"
    stroke="currentColor"
    stroke-width="1.8"
    stroke-linecap="round"
    stroke-linejoin="round"
  />
</svg>`;

/** `L^2`, `L`, `constant`, `1/L` - a formula term's column header. */
function termHeader(exp: number): string {
  if (exp === 0) return 'Constant';
  if (exp === 1) return 'L';
  if (exp === -1) return '1/L';
  return exp < 0 ? `1/L^${-exp}` : `L^${exp}`;
}

/** Evaluate a sparse polynomial Σ coefficient·x^exponent at x. Exponents may be
 * negative (1/load terms), so we can't assume Horner over dense powers. */
function polyval(terms: Array<[number, number]>, x: number): number {
  let acc = 0;
  for (const [exp, coef] of terms) acc += coef * x ** exp;
  return acc;
}

const SUP: Record<string, string> = {
  '-': '⁻', '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴',
  '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹',
};
function superscript(n: number): string {
  return String(n)
    .split('')
    .map((ch) => SUP[ch] ?? ch)
    .join('');
}

/** Format one coefficient at 4 significant figures, trimmed. */
function fmtCoef(v: number): string {
  if (!Number.isFinite(v)) return '0';
  const abs = Math.abs(v);
  if (abs !== 0 && (abs >= 1e6 || abs < 1e-3)) return v.toExponential(2);
  return Number(v.toPrecision(4)).toString();
}

/** `L`, `L²`, `L⁻¹`, … for the variable part of a term (empty for a constant). */
function varPart(exp: number): string {
  if (exp === 0) return '';
  if (exp === 1) return '·L';
  return `·L${superscript(exp)}`;
}

/** Render sparse terms as a human-readable polynomial in L (engine load %). */
function formatFormula(terms: Array<[number, number]>): string {
  const sorted = terms.filter(([, c]) => c !== 0).sort((a, b) => b[0] - a[0]);
  if (!sorted.length) return '0';
  let out = '';
  for (let i = 0; i < sorted.length; i++) {
    const [exp, coef] = sorted[i]!;
    if (i === 0) out += coef < 0 ? '−' : '';
    else out += coef < 0 ? ' − ' : ' + ';
    out += fmtCoef(Math.abs(coef)) + varPart(exp);
  }
  return out;
}

/** Vertical reference lines at the LOAD_GUIDES loads, with a small label. */
function drawLoadGuides(u: uPlot): void {
  const { top, left, width, height } = u.bbox;
  const ctx = u.ctx;
  const d = window.devicePixelRatio || 1;
  const color = getCssVar('--text-dim') || 'rgba(150,150,150,0.7)';
  ctx.save();
  ctx.lineWidth = 1 * d;
  ctx.font = `600 ${Math.round(10 * d)}px system-ui, -apple-system, sans-serif`;
  ctx.textBaseline = 'top';
  ctx.textAlign = 'center';
  for (const load of LOAD_GUIDES) {
    const x = u.valToPos(load, 'x', true);
    if (x < left || x > left + width) continue;
    ctx.globalAlpha = 0.5;
    ctx.strokeStyle = color;
    ctx.setLineDash([4 * d, 4 * d]);
    ctx.beginPath();
    ctx.moveTo(x, top);
    ctx.lineTo(x, top + height);
    ctx.stroke();
    ctx.globalAlpha = 0.85;
    ctx.setLineDash([]);
    ctx.fillStyle = color;
    ctx.fillText(`${load}%`, x, top + 3 * d);
  }
  ctx.restore();
}

/** A faint per-engine load-usage histogram to shade behind the curves. */
interface LoadOverlay {
  edges: number[];
  counts: number[];
  color: string;
}

/** Shade each engine's load-usage histogram as a faint band along the bottom,
 * on the shared engine-load x-axis. Each is normalized to its own peak and
 * drawn at low opacity, so overlapping engines read as tinted context - never
 * competing with the SFOC curves. Drawn in `drawClear` so it sits behind them. */
function drawLoadOverlays(u: uPlot, overlays: LoadOverlay[]): void {
  if (!overlays.length) return;
  const xs = u.scales.x;
  if (xs?.min == null || xs?.max == null) return;
  const { left, top, width, height } = u.bbox;
  const ctx = u.ctx;
  const bandH = height * 0.28;
  const baseY = top + height;
  const inView = (e0: number, e1: number): boolean => e1 >= xs.min! && e0 <= xs.max!;
  ctx.save();
  for (const ov of overlays) {
    // Normalize to the peak within the visible load range only - most 6-month
    // samples sit at ~0% (engine off), which would otherwise dwarf the running
    // distribution we actually want to read.
    let maxC = 0;
    for (let i = 0; i < ov.counts.length; i++) {
      const c = ov.counts[i]!;
      if (Number.isFinite(c) && c > maxC && inView(ov.edges[i]!, ov.edges[i + 1]!)) maxC = c;
    }
    if (maxC <= 0) continue;
    ctx.fillStyle = withAlpha(ov.color, 0.14);
    for (let i = 0; i < ov.counts.length; i++) {
      const c = ov.counts[i]!;
      if (!Number.isFinite(c) || c <= 0) continue;
      const e0 = ov.edges[i]!;
      const e1 = ov.edges[i + 1]!;
      if (!inView(e0, e1)) continue;
      const x0 = Math.max(left, u.valToPos(e0, 'x', true));
      const x1 = Math.min(left + width, u.valToPos(e1, 'x', true));
      const w = x1 - x0;
      if (w <= 0) continue;
      const h = Math.min(1, c / maxC) * bandH;
      ctx.fillRect(x0, baseY - h, w, h);
    }
  }
  ctx.restore();
}

/** The span of the selected fuels' standard SFOC bands (min of mins, max of
 * maxes), so every curve stays visible; null when no selected fuel has one. */
function bandFor(series: PlotSeries[]): [number, number] | null {
  let lo = Infinity;
  let hi = -Infinity;
  for (const s of series) {
    const band = SFOC_BANDS[s.fuel.toUpperCase()];
    if (!band) continue;
    lo = Math.min(lo, band[0]);
    hi = Math.max(hi, band[1]);
  }
  return Number.isFinite(lo) ? [lo, hi] : null;
}

/** The y-scale range for the selected fuels: their band, widened to fit any
 * curve whose fuel has none. */
function yRangeFor(series: PlotSeries[]): uPlot.Scale.Range {
  const band = bandFor(series);
  const hasUnbanded = series.some((s) => !SFOC_BANDS[s.fuel.toUpperCase()]);
  return (_u, dataMin, dataMax) => {
    if (!band) {
      const pad = (dataMax - dataMin) * 0.08 || 1;
      return [dataMin - pad, dataMax + pad];
    }
    let [l, h] = band;
    if (hasUnbanded) {
      if (Number.isFinite(dataMin)) l = Math.min(l, dataMin);
      if (Number.isFinite(dataMax)) h = Math.max(h, dataMax);
    }
    return [l, h];
  };
}

/** Paint every curve's measured points behind it, in its colour, with the
 * gear's scatter alpha and point size - one path per curve, filled once. */
function drawMeasuredScatter(
  u: uPlot,
  layers: Array<{ color: string; points: MeasuredPoints }>,
  alpha: number,
  size: number,
): void {
  const { left, top, width, height } = u.bbox;
  const ctx = u.ctx;
  const d = window.devicePixelRatio || 1;
  const r = Math.max(1, size / 2) * d;
  ctx.save();
  ctx.beginPath();
  ctx.rect(left, top, width, height);
  ctx.clip();
  ctx.globalAlpha = alpha;
  for (const { color, points } of layers) {
    ctx.fillStyle = color;
    ctx.beginPath();
    for (let i = 0; i < points.load.length; i++) {
      const x = u.valToPos(points.load[i]!, 'x', true);
      const y = u.valToPos(points.sfoc[i]!, 'y', true);
      if (x < left || x > left + width || y < top || y > top + height) continue;
      ctx.moveTo(x + r, y);
      ctx.arc(x, y, r, 0, Math.PI * 2);
    }
    ctx.fill();
  }
  ctx.restore();
}

/** A resolved curve to draw, with its origin for labelling/coloring. */
interface PlotSeries {
  ship: string;
  engine: string;
  fuel: string;
  /** The ship-independent half of the label (`DG1 · LNG`), which is what the
   * chart's colour modes group by when several ships are on the chart. */
  column: string;
  color: string;
  label: string;
  /** True when this is a generic fallback curve (drawn dashed, labelled generic). */
  generic: boolean;
  curve: SfocCurve;
  /** The version drawn: in force at the time slicer's moment (or the range's
   *  end), null when the curve had no version yet then. */
  shown: SfocCurveVersion | null;
}

/** The load axis the curves are sampled on. */
function loadAxis(): number[] {
  const xs: number[] = [];
  for (let x = LOAD_MIN; x <= LOAD_MAX; x += LOAD_STEP) xs.push(x);
  return xs;
}

/**
 * The selected curves as a line response, the shape `/api/plot` returns - so
 * Explore's renderer, tooltip and colour modes draw them with nothing
 * SFOC-specific. Each curve is sampled only up to LOAD_PLOT_MAX; beyond that the
 * fit isn't trustworthy, so it is null (a gap) while the axis runs on to
 * LOAD_MAX.
 */
function curvesAsResponse(series: PlotSeries[]): PlotResponse {
  const xs = loadAxis();
  return {
    plot_type: 'line',
    x: xs,
    x_column: 'Engine load (%)',
    x_is_time: false,
    series: series.map((s) => ({
      name: s.label,
      values: xs.map((x) =>
        x > LOAD_PLOT_MAX || !s.shown ? null : polyval(s.shown.terms, x),
      ),
      axis: 'left',
      ship: s.ship,
      column: s.column,
    })),
    source_rows: 0,
    returned_points: xs.length,
    first_x: xs[0] ?? null,
    last_x: xs[xs.length - 1] ?? null,
    null_counts: {},
    color_by: null,
    color_values: null,
    fits: [],
  };
}

@customElement('sfoc-viewer')
export class SfocViewer extends LightElement {
  private store = new StoreController(this, [
    activeTab,
    activeModelTab,
    modelShips,
    modelStart,
    modelEnd,
    sfocData,
    sfocSelected,
    sfocPlotState,
    sfocMeasured,
    sfocTables,
    sfocLayout,
    measuredCache,
  ]);

  /** The drawn charts: one in Overlay, one per ship in Grid - each with the
   *  ship it draws (null for Overlay) and the box it is sized to. */
  private panels: Array<{ ship: string | null; chart: TileChart; mount: HTMLElement }> = [];
  /** Bumped per draw, so a draw that resolves after a newer one is dropped. */
  private drawGen = 0;
  private resizeObs: ResizeObserver | null = null;
  private themeObs: MutationObserver | null = null;

  /** Table sort: column ('curve' or a load %) and direction. null = input order. */
  @state() private sortKey: number | 'curve' | null = null;
  @state() private sortDir: 'asc' | 'desc' = 'asc';

  override connectedCallback(): void {
    super.connectedCallback();
    // Recolor the plot when the light/dark theme toggles (a class flip on
    // <html> that no atom tracks).
    // The fuel colours are per theme, so the swatches re-render with the chart.
    this.themeObs = new MutationObserver(() => this.requestUpdate());
    this.themeObs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
    this.offSlice = sfocSlice.subscribe(() => this.onSlice());
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.themeObs?.disconnect();
    this.themeObs = null;
    this.resizeObs?.disconnect();
    this.resizeObs = null;
    this.destroyPanels();
    this.offSlice?.();
    this.offSlice = null;
  }

  private offSlice: (() => void) | null = null;
  /** What the drawn chart was built from; a render that changes none of it
   *  (a slicer move, a table sort) leaves the chart as it is. */
  private builtFrom: unknown[] = [];

  /**
   * A slicer move: the same chart, new curve versions and new points. The
   * curves' data is swapped in place and the measured layers - which read the
   * slice when they paint - repaint with it; nothing is rebuilt, so Play stays
   * smooth. The tables re-render for the versions they show.
   */
  private onSlice(): void {
    const all = this.buildSeries();
    for (const p of this.panels) {
      const u = p.chart.uplot;
      if (!u) continue;
      const data = curvesAsResponse(p.ship ? all.filter((s) => s.ship === p.ship) : all);
      u.setData([data.x, ...data.series.map((ser) => ser.values)] as uPlot.AlignedData);
    }
    this.requestUpdate();
  }

  private destroyPanels(): void {
    for (const p of this.panels) p.chart.destroy();
    this.panels = [];
  }

  /** The day the drawn curves are as of: the slicer window's end, or else the
   *  curve date - the range's last day. */
  private curveDate(): string {
    const max = sfocSlice.value.max;
    return max == null ? sfocDate.value : curveDateFor(new Date(max * 1000).toISOString().slice(0, 19));
  }

  private sliceCache = new WeakMap<MeasuredPoints, { key: string; points: MeasuredPoints }>();

  /** A curve's measured points inside the slicer window (all of them untouched). */
  private sliced(points: MeasuredPoints): MeasuredPoints {
    const { min, max } = sfocSlice.value;
    if ((min == null && max == null) || !points.time) return points;
    const key = `${min}|${max}`;
    const hit = this.sliceCache.get(points);
    if (hit?.key === key) return hit.points;
    const lo = min ?? -Infinity;
    const hi = max ?? Infinity;
    const load: number[] = [];
    const sfoc: number[] = [];
    const time: number[] = [];
    for (let i = 0; i < points.time.length; i++) {
      const t = points.time[i]!;
      if (t < lo || t > hi) continue;
      load.push(points.load[i]!);
      sfoc.push(points.sfoc[i]!);
      time.push(t);
    }
    const out = { ...points, load, sfoc, time };
    this.sliceCache.set(points, { key, points: out });
    return out;
  }

  /** Whether SFOC is the model on screen - the only time it fetches. */
  private get showing(): boolean {
    return activeTab.value === 'modelviewer' && activeModelTab.value === 'sfoc';
  }

  override updated(): void {
    if (!this.showing) return;
    ensurePickedLoaded();
    const series = this.buildSeries();
    this.ensureMeasured(series);
    const from = [
      series.map((s) => `${s.label}|${s.color}`).join(','),
      sfocMeasured.value,
      measuredCache.value,
      sfocPlotState.value,
      document.documentElement.className,
      modelStart.value,
      modelEnd.value,
      sfocLayout.value,
    ];
    const same =
      this.panels.length > 0 &&
      from.length === this.builtFrom.length &&
      from.every((v, i) => v === this.builtFrom[i]);
    if (same) return;
    this.builtFrom = from;
    this.rebuildPlot();
  }

  /** Fetch the measured points behind each curve on the chart, once per curve
   *  and window - every measured view draws from the same points. */
  private ensureMeasured(series: PlotSeries[]): void {
    if (sfocMeasured.value === 'off') return;
    for (const s of series) {
      // A generic curve shown on DG1 is still DG1's: its measured points apply.
      // Only the engine-less "All" row has no engine column to read.
      if (s.engine !== GENERIC_ENGINE) {
        ensureMeasured(s.ship, s.engine, s.fuel, modelStart.value, modelEnd.value);
      }
    }
  }

  /** Each curve's measured points as far as they have arrived, and what is
   *  still loading or could not be had - for the drawing and for the note. */
  private measured(series: PlotSeries[]): {
    layers: Array<{ series: PlotSeries; points: MeasuredPoints }>;
    pending: number;
    total: number;
    missing: string[];
  } {
    const layers: Array<{ series: PlotSeries; points: MeasuredPoints }> = [];
    const missing: string[] = [];
    let pending = 0;
    let total = 0;
    for (const s of series) {
      if (s.engine === GENERIC_ENGINE || !measuredColumn(s.engine, s.fuel)) {
        missing.push(`${s.label} (not tied to one engine)`);
        continue;
      }
      total++;
      const entry = measuredCache.value.get(
        measuredKey(s.ship, s.engine, s.fuel, modelStart.value, modelEnd.value),
      );
      if (!entry || entry === 'loading') pending++;
      else if ('error' in entry) missing.push(`${s.label} (${entry.error})`);
      else if (!entry.load.length) missing.push(`${s.label} (no measured points in range)`);
      else layers.push({ series: s, points: entry });
    }
    return { layers, pending, total, missing };
  }

  override render(): TemplateResult {
    const series = this.buildSeries();
    const mode = sfocMeasured.value;
    const m = mode === 'off' ? null : this.measured(series);
    const points = m ? m.layers.reduce((n, l) => n + this.sliced(l.points).load.length, 0) : 0;
    const slice = sfocSlice.value;
    const narrowed = slice.min != null || slice.max != null;
    const tables = sfocTables.value;
    return html`
      <div class="sfoc-layout">
        <div class="sfoc-main scroll-thin">
          <div class="card plot-card-wrap sfoc-chart-card" data-tour="sfoc-chart">
            <h2>
              <span class="chart-header-left">
                <span>SFOC vs engine load</span>
                ${m && m.layers.length
                  ? html`<span class="sfoc-subnote"
                      >${mode === 'load' ? 'shaded = load distribution' : 'behind = measured SFOC'},
                      ${points.toLocaleString()} points in the ${narrowed ? 'time window' : 'date range'}</span
                    >`
                  : nothing}
                ${series.length
                  ? html`<span class="sfoc-subnote">curves as of ${this.curveDate()}</span>`
                  : nothing}
                ${series.some((s) => s.generic)
                  ? html`<span class="sfoc-subnote">dashed = generic fallback</span>`
                  : nothing}
              </span>
              <div class="chart-header-actions">
                <plot-settings-menu .target=${sfocChartStyle}></plot-settings-menu>
              </div>
            </h2>
            ${m && m.pending
              ? html`<div
                  class="sfoc-measured-progress"
                  role="progressbar"
                  aria-label="Loading measured SFOC"
                  aria-valuemin="0"
                  aria-valuemax=${m.total}
                  aria-valuenow=${m.total - m.pending}
                >
                  <div class="progress-bar-track">
                    <div
                      class="progress-bar-fill"
                      style="width:${Math.round(((m.total - m.pending) / m.total) * 100)}%"
                    ></div>
                  </div>
                </div>`
              : nothing}
            <div class="sfoc-plot-body" id="sfoc-plot"></div>
            ${series.length ? this.renderSlicer(series, m?.layers ?? []) : nothing}
            ${m && m.missing.length && !m.pending
              ? html`<p class="sfoc-measured-missing" title=${m.missing.join('\n')}>
                  No measured SFOC for ${m.missing.length === 1
                    ? m.missing[0]
                    : `${m.missing.length} curves - ${m.missing.join('; ')}`}
                </p>`
              : nothing}
          </div>
          ${series.length && tables.values ? this.renderTable(series) : nothing}
          ${series.length && tables.formulas ? this.renderFormulas(series) : nothing}
        </div>
      </div>
    `;
  }

  /**
   * The time slicer under the chart, as Explore's sits under its chart. Its
   * histogram is the loaded points' times; its ticks are the days a selected
   * curve's next version started.
   */
  private renderSlicer(
    series: PlotSeries[],
    layers: Array<{ series: PlotSeries; points: MeasuredPoints }>,
  ): TemplateResult {
    const epoch = (v: string): number =>
      Date.parse(/(?:Z|[+-]\d\d:\d\d)$/i.test(v) ? v : `${v}Z`) / 1000;
    const byDay = new Map<string, string[]>();
    for (const s of series) {
      for (const v of (s.curve.versions ?? []).slice(1)) {
        if (!v.valid_from) continue;
        byDay.set(v.valid_from, [...(byDay.get(v.valid_from) ?? []), s.label]);
      }
    }
    const ticks: SliceTick[] = [...byDay].map(([day, labels]) => ({
      t: epoch(`${day}T00:00:00`),
      label: `${day}: new version of ${labels.join(', ')}`,
    }));
    return html`<sfoc-time-slicer
      data-tour="sfoc-slicer"
      .range=${{ start: epoch(modelStart.value), end: epoch(modelEnd.value) }}
      .times=${layers.map((l) => l.points.time ?? [])}
      .ticks=${ticks}
    ></sfoc-time-slicer>`;
  }

  /** How much history stands behind a version: its drawable predecessors, and
   *  the stored ones without formulas, which exist but cannot be drawn. */
  private olderNote(s: PlotSeries): TemplateResult | typeof nothing {
    const versions = s.curve.versions?.length ?? 0;
    const blank = s.curve.unsupported_versions ?? 0;
    if (versions <= 1 && !blank) return nothing;
    const parts: string[] = [];
    if (versions > 1) parts.push(`${versions} versions`);
    if (blank) parts.push(`${blank} without formula`);
    return html`<span class="sfoc-version-note">${parts.join(', ')}</span>`;
  }

  /** The fitted formula of each curve, with the version it is and how well it
   *  fits - one row per curve. */
  private renderFormulas(series: PlotSeries[]): TemplateResult {
    return html`
      <div class="sfoc-table-card">
        <div class="sfoc-card-head">
          <div class="sfoc-card-title">Fitted formulas - SFOC as a function of engine load L (%)</div>
          ${this.renderDownload('Download the fitted formulas as an Excel workbook', () =>
            this.downloadFormulas(series),
          )}
        </div>
        <div class="sfoc-table-scroll scroll-thin">
          <table class="sfoc-table sfoc-formula-table">
            <thead>
              <tr>
                <th class="sfoc-th-curve">Curve</th>
                <th class="sfoc-th-text">Valid from</th>
                <th class="sfoc-th-text">SFOC (g/kWh) =</th>
                <th>R²</th>
              </tr>
            </thead>
            <tbody>
              ${series.map(
                (s) => html`<tr>
                  <th class="sfoc-th-curve">
                    <span class="sfoc-swatch" style="background:${s.color}"></span>${s.label}
                  </th>
                  <td class="sfoc-td-text">
                    ${s.shown?.valid_from ?? '-'}${this.olderNote(s)}
                  </td>
                  <td class="sfoc-td-text sfoc-formula-eq">
                    ${s.shown ? formatFormula(s.shown.terms) : 'no version in force yet'}
                  </td>
                  <td>${s.shown?.r2 != null ? s.shown.r2.toFixed(3) : '-'}</td>
                </tr>`,
              )}
            </tbody>
          </table>
        </div>
      </div>
    `;
  }

  /* ── Excel downloads ──────────────────────────────────── */

  private renderDownload(title: string, run: () => void): TemplateResult {
    return html`<button class="chart-icon-btn sfoc-download" title=${title} @click=${run}>
      ${DOWNLOAD_GLYPH} Excel
    </button>`;
  }

  /** `sfoc-values_2024-01-01_2024-02-01.xlsx`: what, and over which range. */
  private fileName(what: string): string {
    const day = (v: string): string => v.slice(0, 10);
    return `sfoc-${what}_${day(modelStart.value)}_${day(modelEnd.value)}.xlsx`;
  }

  /** The curve's identity, split into columns so the sheet can be filtered. */
  private identity(s: PlotSeries): XlsxCell[] {
    return [s.label, shipLabel(s.ship), s.engine, s.fuel, s.generic ? 'Generic' : 'Ship-specific'];
  }

  /**
   * The values table as shown, in its current sort, plus the version each row
   * was evaluated from - the slicer can make that differ from curve to curve,
   * and a sheet without it would not say which curve it is a table of.
   */
  private downloadValues(rows: PlotSeries[]): void {
    const header: XlsxCell[] = [
      'Curve',
      'Ship',
      'Engine',
      'Fuel',
      'Curve type',
      'Valid from',
      ...TABLE_LOADS.map((l) => `SFOC at ${l}% load (g/kWh)`),
    ];
    const body = rows.map((s): XlsxCell[] => [
      ...this.identity(s),
      s.shown?.valid_from ?? null,
      ...TABLE_LOADS.map((l) =>
        s.shown ? { v: polyval(s.shown.terms, l), fmt: '0.0' } : null,
      ),
    ]);
    downloadXlsx(this.fileName('values'), [{ name: 'SFOC by load', rows: [header, ...body] }]);
  }

  /**
   * The formulas: the readable equation, R², and each coefficient in its own
   * column, so the curve can be rebuilt in a spreadsheet rather than retyped.
   */
  private downloadFormulas(series: PlotSeries[]): void {
    const exps = [
      ...new Set(series.flatMap((s) => (s.shown?.terms ?? []).map(([exp]) => exp))),
    ].sort((a, b) => b - a);
    const header: XlsxCell[] = [
      'Curve',
      'Ship',
      'Engine',
      'Fuel',
      'Curve type',
      'Valid from',
      'SFOC (g/kWh) =',
      'R²',
      ...exps.map((e) => `Coefficient ${termHeader(e)}`),
    ];
    const body = series.map((s): XlsxCell[] => {
      const coef = new Map((s.shown?.terms ?? []).map(([exp, c]) => [exp, c]));
      return [
        ...this.identity(s),
        s.shown?.valid_from ?? null,
        s.shown ? formatFormula(s.shown.terms) : 'no version in force yet',
        s.shown?.r2 != null ? { v: s.shown.r2, fmt: '0.000' } : null,
        ...exps.map((e) => coef.get(e) ?? null),
      ];
    });
    downloadXlsx(this.fileName('formulas'), [{ name: 'Fitted formulas', rows: [header, ...body] }]);
  }

  /** Click a header to sort by it; clicking the active column flips direction. */
  private sortBy(key: number | 'curve'): void {
    if (this.sortKey === key) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortKey = key;
      this.sortDir = 'asc';
    }
  }

  /** SFOC value table, one row per curve and one column per 10% load step. */
  private renderTable(series: PlotSeries[]): TemplateResult {
    const rows = this.sortedRows(series);
    const caret = (key: number | 'curve'): string =>
      this.sortKey === key ? (this.sortDir === 'asc' ? ' ▲' : ' ▼') : '';
    const cls = (key: number | 'curve'): string =>
      this.sortKey === key ? 'sorted' : '';
    const ariaSort = (key: number | 'curve'): 'ascending' | 'descending' | 'none' =>
      this.sortKey === key ? (this.sortDir === 'asc' ? 'ascending' : 'descending') : 'none';
    return html`
      <div class="sfoc-table-card" data-tour="sfoc-values-table">
        <div class="sfoc-card-head">
          <div class="sfoc-card-title">SFOC by engine load (g/kWh)</div>
          ${this.renderDownload('Download this table as an Excel workbook', () =>
            this.downloadValues(this.sortedRows(series)),
          )}
        </div>
        <div class="sfoc-table-scroll scroll-thin">
          <table class="sfoc-table">
            <thead>
              <tr>
                <th
                  class="sfoc-th-curve sfoc-th-sort ${cls('curve')}"
                  aria-sort=${ariaSort('curve')}
                  @click=${() => this.sortBy('curve')}
                >
                  Curve${caret('curve')}
                </th>
                ${TABLE_LOADS.map(
                  (l) => html`<th
                    class="sfoc-th-sort ${cls(l)}"
                    aria-sort=${ariaSort(l)}
                    @click=${() => this.sortBy(l)}
                  >
                    ${l}%${caret(l)}
                  </th>`,
                )}
              </tr>
            </thead>
            <tbody>
              ${rows.map(
                (s) => html`<tr>
                  <th class="sfoc-th-curve">
                    <span class="sfoc-swatch" style="background:${s.color}"></span>${s.label}
                  </th>
                  ${TABLE_LOADS.map(
                    (l) => html`<td class=${this.sortKey === l ? 'sorted' : ''}>
                      ${s.shown ? formatNum(polyval(s.shown.terms, l)) : '-'}
                    </td>`,
                  )}
                </tr>`,
              )}
            </tbody>
          </table>
        </div>
      </div>
    `;
  }

  /** Order the curve rows by the active sort column (a copy - never the plot's). */
  private sortedRows(series: PlotSeries[]): PlotSeries[] {
    const key = this.sortKey;
    if (key == null) return series;
    const dir = this.sortDir === 'asc' ? 1 : -1;
    return [...series].sort((a, b) => {
      if (key === 'curve') return dir * a.label.localeCompare(b.label);
      const at = (s: PlotSeries): number => (s.shown ? polyval(s.shown.terms, key) : NaN);
      return dir * (at(a) - at(b));
    });
  }

  // ── The chart (imperative, through Explore's renderer) ───────────────────

  /** Resolve selected keys into drawable series (stable ship/engine/fuel order),
   * coloured exactly as the renderer will colour them. */
  private buildSeries(): PlotSeries[] {
    const selected = sfocSelected.value;
    if (selected.size === 0) return [];
    const data = sfocData.value;

    const out: Array<Omit<PlotSeries, 'color'>> = [];
    for (const ship of modelShips.value) {
      const sd = data[ship];
      if (!sd) continue;
      const m = shipMatrix(sd);
      for (const row of m.rows) {
        for (const fuel of m.fuels) {
          if (!row.fuels.has(fuel)) continue;
          const key = curveKey(ship, row.engine, fuel);
          if (!selected.has(key)) continue;
          const curve = getCurve(sd, row.engine, fuel);
          if (!curve) continue;
          const column = row.engine === GENERIC_ENGINE ? fuel : `${row.engine} · ${fuel}`;
          out.push({
            ship,
            engine: row.engine,
            fuel,
            column,
            label: `${ship} · ${column}${curve.generic ? ' · generic' : ''}`,
            generic: curve.generic,
            curve,
            shown: null,
          });
        }
      }
    }
    // Fuel sets the hue (the maritime palette, visual-identity.md §8.4); each
    // curve on the same fuel - another engine, another ship - takes a shade of
    // it. A fuel outside the palette falls back to the chart's series colours.
    const colorOf = buildSeriesColorer(
      sfocPlotState.value,
      out.map((s) => ({ name: s.label, ship: s.ship, column: s.column })),
      null,
    );
    const date = this.curveDate();
    for (const s of out) s.shown = versionAt(s.curve, date);
    const onFuel = new Map<string, number>();
    for (const s of out) onFuel.set(s.fuel, (onFuel.get(s.fuel) ?? 0) + 1);
    const seen = new Map<string, number>();
    return out.map((s, i) => {
      const token = fuelVar(s.fuel);
      const base = token ? getCssVar(token) : '';
      if (!base) return { ...s, color: colorOf({ name: s.label, ship: s.ship, column: s.column }, i) };
      const idx = seen.get(s.fuel) ?? 0;
      seen.set(s.fuel, idx + 1);
      return { ...s, color: fuelShade(base, idx, onFuel.get(s.fuel)!) };
    });
  }

  /** A panel's chart size: its box, less the legend uPlot puts under the canvas. */
  private panelSize(mount: HTMLElement, chart: TileChart | null): { width: number; height: number } {
    const legend = chart?.legendEl();
    let legendH = 0;
    if (legend && legend.offsetParent !== null) {
      const r = legend.getBoundingClientRect();
      const s = getComputedStyle(legend);
      legendH =
        Math.ceil(r.height) +
        Math.ceil(parseFloat(s.marginTop) || 0) +
        Math.ceil(parseFloat(s.marginBottom) || 0);
    }
    return {
      width: Math.max(120, mount.clientWidth),
      height: Math.max(120, mount.clientHeight - legendH),
    };
  }

  /** Panels per row in Grid - Explore's rule, and its "Panels per row" setting. */
  private applyColumns(root: HTMLElement, count: number): void {
    const cols = gridColumns(root.clientWidth || 600, count, false, sfocPlotState.value.style.grid_columns);
    root.style.gridTemplateColumns = `repeat(${cols}, minmax(0, 1fr))`;
  }

  private resizePanels(root: HTMLElement): void {
    if (this.panels.length > 1) this.applyColumns(root, this.panels.length);
    for (const p of this.panels) p.chart.resize(this.panelSize(p.mount, p.chart));
  }

  private ensureResizeObs(root: HTMLElement): void {
    if (this.resizeObs) return;
    this.resizeObs = new ResizeObserver(() => this.resizePanels(root));
    this.resizeObs.observe(root);
  }

  /**
   * What only an SFOC chart has, on top of the chart Explore would draw: the
   * SFOC axis title, the per-fuel y-band (unless the gear asked for a log
   * scale), generic fallbacks dashed, the 50/80% load guides - and the measured
   * data behind the curves, as the sidebar's Measured choice says.
   */
  private decorate(opts: uPlot.Options, series: PlotSeries[], all: PlotSeries[] = series): void {
    const style = sfocPlotState.value;
    const yAxis = opts.axes?.[1];
    if (yAxis) yAxis.label = 'SFOC (g/kWh)';
    const y = opts.scales?.y;
    // Every panel of a grid on the same scale - the whole selection's band - so
    // two ships read against each other rather than each filling its own box.
    if (y && !style.style.log_scale) y.range = yRangeFor(all);
    series.forEach((s, i) => {
      const opt = opts.series[i + 1];
      if (!opt) return;
      // The fuel colours, not the renderer's palette - the legend and tooltip
      // read the stroke, so they follow.
      opt.stroke = s.color;
      if (opt.points) opt.points = { ...opt.points, stroke: s.color, fill: s.color };
      if (s.generic) opt.dash = [6, 4];
    });

    // Each layer paints from the points inside the slicer window as it is when
    // it paints, so a slicer move repaints without a rebuild.
    const behind: Array<(u: uPlot) => void> = [];
    const mode = sfocMeasured.value;
    const layers = mode === 'off' ? [] : this.measured(series).layers;
    const now = (): Array<{ series: PlotSeries; points: MeasuredPoints }> =>
      layers.map((l) => ({ series: l.series, points: this.sliced(l.points) }));
    if (layers.length && mode === 'scatter') {
      behind.push((u) =>
        drawMeasuredScatter(
          u,
          now().map((l) => ({ color: l.series.color, points: l.points })),
          style.style.scatter_alpha,
          style.style.scatter_point_size,
        ),
      );
    } else if (layers.length && mode === 'heatmap') {
      // The grid is the whole range's, so cells keep their place as the window
      // moves; only their counts change.
      let yr = bandFor(all);
      if (!yr) {
        const all = layers.flatMap((l) => l.points.sfoc);
        yr = [Math.min(...all), Math.max(...all)];
      }
      const colors = themeColors();
      // In a grid, one colour scale for every panel: the busiest cell of any
      // ship is the top of the ramp, so a quiet ship does not look as dense.
      const everyone =
        all !== series ? this.measured(all).layers : null;
      behind.push((u) => {
        const grid = binHeatmap(
          now().map((l) => l.points),
          [LOAD_MIN, LOAD_MAX],
          yr!,
        );
        if (!grid) return;
        if (everyone) {
          const whole = binHeatmap(
            everyone.map((l) => this.sliced(l.points)),
            [LOAD_MIN, LOAD_MAX],
            yr!,
          );
          if (whole) {
            grid.z_min = whole.z_min;
            grid.z_max = whole.z_max;
          }
        }
        heatmapDrawHook(grid, colors, style)(u);
      });
    } else if (layers.length && mode === 'load') {
      // One histogram per engine, coloured like its first curve.
      behind.push((u) => {
        const byEngine = new Map<string, { color: string; sets: MeasuredPoints[] }>();
        for (const l of now()) {
          const key = `${l.series.ship}‖${l.series.engine}`;
          const entry = byEngine.get(key) ?? { color: l.series.color, sets: [] };
          entry.sets.push(l.points);
          byEngine.set(key, entry);
        }
        const overlays: LoadOverlay[] = [...byEngine.values()].map((e) => ({
          ...binLoad(e.sets, [LOAD_MIN, LOAD_MAX]),
          color: e.color,
        }));
        drawLoadOverlays(u, overlays);
      });
    }

    const hooks = (opts.hooks ??= {});
    hooks.drawClear = [...(hooks.drawClear ?? []), ...behind];
    hooks.draw = [...(hooks.draw ?? []), drawLoadGuides, drawBranding];
  }

  private rebuildPlot(): void {
    const root = this.querySelector<HTMLElement>('#sfoc-plot');
    if (!root) return;

    const gen = ++this.drawGen;
    this.destroyPanels();
    root.replaceChildren();
    root.classList.remove('chart-grid', 'sfoc-grid');
    root.style.gridTemplateColumns = '';

    const all = this.buildSeries();
    if (all.length === 0) {
      root.appendChild(this.emptyState());
      return;
    }

    this.ensureResizeObs(root);
    // The legend and tooltip size from the chart's text setting, as Explore's do.
    applyChartFontVar(root, sfocPlotState.value.style.font_size);

    // Grid: the same chart once per ship, in selection order, with the colours
    // it has in the overlay. Overlay: one chart for all of them.
    const ships = [...new Set(all.map((s) => s.ship))];
    const grid = sfocLayout.value === 'grid' && ships.length > 1;
    const groups = grid
      ? ships.map((ship) => ({ ship, series: all.filter((s) => s.ship === ship) }))
      : [{ ship: null as string | null, series: all }];
    if (grid) {
      root.classList.add('chart-grid', 'sfoc-grid');
      this.applyColumns(root, groups.length);
    }

    const built = groups.map((g) => {
      const container = grid ? document.createElement('div') : root;
      if (grid) {
        container.className = 'chart-grid-panel';
        const title = document.createElement('div');
        title.className = 'chart-grid-title';
        title.textContent = shipLabel(g.ship!);
        title.title = g.ship!;
        container.appendChild(title);
        root.appendChild(container);
      }
      const mount = document.createElement('div');
      mount.className = 'sfoc-plot-mount';
      const tip = document.createElement('div');
      tip.className = 'uplot-tooltip';
      container.append(mount, tip);
      return { g, mount, tip, container };
    });

    void Promise.all(
      built.map(({ g, mount, tip, container }) =>
        renderTile(
          { mount, tooltip: tip, container },
          curvesAsResponse(g.series),
          sfocPlotState.value,
          this.panelSize(mount, null),
          {
            decorate: (opts) => this.decorate(opts, g.series, all),
            // One crosshair across the panels: the same load on every ship.
            syncKey: grid ? 'sfoc' : null,
          },
        ).then((chart) => ({ ship: g.ship, chart, mount })),
      ),
    ).then((drawn) => {
      const ready = drawn.flatMap((d) => (d.chart ? [{ ship: d.ship, chart: d.chart, mount: d.mount }] : []));
      if (gen !== this.drawGen) {
        for (const d of ready) d.chart.destroy();
        return;
      }
      this.panels = ready;
      // The legends exist only once uPlot has built them; size again without
      // them, and once more after layout settles.
      this.resizePanels(root);
      requestAnimationFrame(() => {
        if (gen === this.drawGen) this.resizePanels(root);
      });
    });
  }

  /** Which kind of empty: nothing picked, nothing selected, or still loading. */
  private emptyState(): HTMLElement {
    const [text, sub] = !modelShips.value.length
      ? ['No ships picked', 'Pick one or more ships in the panel to list their engines and fuels.']
      : sfocSelected.value.size
        ? ['Loading curves…', '']
        : ['No curves selected', 'Tick an engine, a fuel or a whole ship in the panel to plot its SFOC.'];
    const el = document.createElement('div');
    el.className = 'plot-empty';
    for (const [cls, content] of [
      ['empty-icon', '◖'],
      ['empty-text', text],
      ['empty-sub', sub],
    ] as const) {
      if (!content) continue;
      const span = document.createElement('span');
      span.className = cls;
      span.textContent = content;
      el.appendChild(span);
    }
    return el;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sfoc-viewer': SfocViewer;
  }
}
